import PageLayout from '../../wrappers/PageLayout';
import './SellerDashboard.scss';
import Integrations from '@/components/Integrations/Integrations';
import { useEffect, useMemo, useState } from 'react';
import { NormalizedSellerFilingReport } from '@/core/types';
import {
  getBillbee,
  getClientReports,
  getCompletedFiling,
  getDataUnits,
  getDataUnitsStats,
  getEasybillTokenStatus,
  getJTLCredentials,
  getOngoingFiling,
  getRegisteredVATIds,
  getYearPayments,
  postClientAmazonConnect,
} from '@/core/api';
import { Loader } from '@/components/Loader/Loader';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { getUTCDate } from '@/core/helpers';
import SellerCalendarCollapse from '@/components/SellerCalendarCollapse';
import OpenTasks from '@/components/OpenTasks/OpenTasks';
import SellerChart from '@/components/SellerChart/SellerChart';
import { useFlags } from 'launchdarkly-react-client-sdk';
import MerryChristmasBanner from '@/components/MerryChristmasBanner';
import {
  differenceInCalendarMonths,
  endOfMonth,
  format,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { normalizeDateFE } from '@/core/normalization';
import Avatar from '@/components/Participants/Avatar';
import Shimmer from '@/components/Shimmer/Shimmer';
import {
  activeCompanySelector,
  companiesSelector,
  isOSSSelfServiceSelector,
  queryKeySelector,
  userSelector,
} from '@/core/store/user/userSlice';
import useNotification from '../../hooks/useNotification';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AuthedRoutesEnum,
  DataCorrectionStatusEnum,
  DUItemStatusEnum,
  PricingModelEnum,
  ReportSubTypesEnum,
  SellerFilingReportStatusEnum,
} from '@/core/enums';
import PaymentInstructionModal from '@/components/PaymentInstructionModal/PaymentInstructionModal';
import SubmitBOPModal from '@/components/SubmitBOPModal';
import usePaymentInstructionModal from './SellerFiling/usePaymentInstructionModal';
import { useSubmitBOPModal } from './SellerFiling/useSubmitBOPModal';
import NewYearIcon from '@/icons/newyeartoys.svg?react';
import { getColor } from '@/components/Participants/Participants.helpers';
import { useSellerIntegrations } from '@/components/Integrations/useSellerIntegrations';
import { useMU } from '../../context/useMU';
import { usePP } from '../../context/usePP';
import { OSSSeSeReportPreview } from '@/components/OSSSelfServiceWidget/OSSSeSeReportPreview';
import { getCurrentOSSSeSeReport } from '../OSSFiles.helpers';
import FeedbackWidget from '@/components/FeedbackWidget/FeedbackWidget';
import NotificationWidget from '@/components/NotificationWidget/NotificationWidget';
export const STATE_CONSTANT = 'customer-app';

export default function SellerDashboard() {
  const activeCompany = useAppSelector(activeCompanySelector);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const {
    vs4972NewYear2024,
    vs5919CpNotificationWidget,
    vs3424MerryChristmas,
    vs6430DeReportsSettings,
  } = useFlags();

  const [isConnectingAmazon, setIsConnectingAmazon] = useState(false);
  const navigate = useNavigate();
  const locaton = useLocation();
  const params = new URLSearchParams(locaton.search);
  const state = params.get('state');
  const spapi_oauth_code = params.get('spapi_oauth_code');
  const selling_partner_id = params.get('selling_partner_id');
  const user = useAppSelector(userSelector);
  const queryKey = useAppSelector(queryKeySelector);
  const companies = useAppSelector(companiesSelector);
  const { showNotification } = useNotification();
  const currDate = new Date();

  const {
    onClickConfirmPayment,
    isPaymentInstructionModalOpened,
    closePaymentInstructionModal,
    reportToConfirmPayment,
    handleOpenPaymentInstructionModal,
    isConfirmingPayment,
  } = usePaymentInstructionModal();

  const {
    reportToSubmit,
    openSubmitBOPModal,
    isSubmitBOPModalOpened,
    closeSubmitBOPModal,
    submitReport,
  } = useSubmitBOPModal('SellerDashboard', async () => {
    const [ongoingRes, completedRes] = await Promise.all([
      getOngoingFiling(activeCompany?.company_id),
      getCompletedFiling(activeCompany?.company_id),
    ]);

    queryClient.setQueryData(['SellerOngoingFiling', queryKey], ongoingRes);
    queryClient.setQueryData(
      ['SellerPastPeriodsFiling', queryKey],
      completedRes
    );
  });

  const [
    reports,
    ongoingFiling,
    completedFiling,
    easybill,
    yearPayments,
    billbee,
    jtlCredentials,
    currDUStats,
    registeredVATIds,
  ] = useQueries({
    queries: [
      {
        queryKey: ['SellerReports', queryKey],
        queryFn: () => getClientReports(activeCompany?.company_id),
      },
      {
        queryKey: ['SellerOngoingFiling', queryKey],
        queryFn: () => getOngoingFiling(activeCompany?.company_id),
      },
      {
        queryKey: ['SellerPastPeriodsFiling', queryKey],
        queryFn: () => getCompletedFiling(activeCompany?.company_id),
      },
      {
        queryKey: ['SellerEasybill', queryKey],
        queryFn: () => getEasybillTokenStatus(activeCompany?.company_id),
      },
      {
        queryKey: ['SellerYearPayments', queryKey],
        queryFn: () => getYearPayments(activeCompany?.company_id),
      },
      {
        queryKey: ['SellerBillbee', queryKey],
        queryFn: () => getBillbee(activeCompany?.company_id),
      },
      {
        queryKey: ['SellerJtlCredentials', queryKey],
        queryFn: () => getJTLCredentials(activeCompany?.company_id),
      },
      {
        queryKey: ['SellerDashboardDUStats', queryKey],
        queryFn: () => {
          const date = new Date();

          return getDataUnitsStats(
            activeCompany?.company_id,
            format(startOfMonth(subMonths(date, 1)), 'yyyy-MM-dd'),
            format(endOfMonth(date), 'yyyy-MM-dd')
          );
        },
      },
      {
        queryKey: ['SellerRegisteredVATIds', queryKey],
        queryFn: () => getRegisteredVATIds(activeCompany?.company_id),
      },
    ],
  });

  const {
    isLoading: isLoadingIntegrations,
    integrations,
    refetchIntegrations,
  } = useSellerIntegrations();

  const { isLoadingUploadedFiles } = useMU();

  const { data: dataUnits, isLoading: isLoadingDataUnits } = useQuery({
    queryKey: ['SellerDataUnits', queryKey],
    queryFn: () => getDataUnits(activeCompany?.company_id!),
  });

  const ppData = usePP();

  const isLoadingPP = ppData.isLoadingPP;

  const countPP = ppData.countLastMonthPP;

  useEffect(() => {
    if (
      state &&
      state.includes(STATE_CONSTANT) &&
      spapi_oauth_code &&
      selling_partner_id
    ) {
      const [user_id, company_id] = state
        .replace(STATE_CONSTANT, '')
        .split('.');
      if (
        companies.find((c) => c.company_id === company_id) &&
        user_id === user?.Id
      ) {
        setIsConnectingAmazon(true);
        postClientAmazonConnect({
          seller_id: selling_partner_id,
          oauth_code: spapi_oauth_code,
          company_id,
        })
          .then(() => {
            showNotification({
              text: t('Merchant token *seller_id* connected').replace(
                '*seller_id*',
                selling_partner_id
              ),
              type: 'success',
            });
          })
          .catch((err) => {
            console.error(err);
          })
          .finally(() => {
            setIsConnectingAmazon(false);
            refetchIntegrations();
            navigate('/', { replace: true });
          });
      }
    }

    // eslint-disable-next-line
  }, [state, spapi_oauth_code, selling_partner_id]);

  const ongoingFilingData = ongoingFiling.data;
  const completedFilingData = completedFiling.data;

  const reportsToConfirmPayment: NormalizedSellerFilingReport[] = useMemo(
    () =>
      [...(ongoingFilingData || []), ...(completedFilingData || [])].filter(
        (r) =>
          !!r.payment_instruction &&
          !r.payment_instruction.confirmed &&
          !r.payment_instruction.is_sepa &&
          !r.payment_instruction.is_null_report &&
          r.payment_instruction.amount.amount > 0 &&
          !r.payment_instruction.cancelled
      ),
    [ongoingFilingData, completedFilingData]
  );

  const reportsToConfirmAgenda: NormalizedSellerFilingReport[] = useMemo(
    () =>
      [...(ongoingFilingData || []), ...(completedFilingData || [])].filter(
        (r) =>
          r.country.short_name === 'DE' &&
          r.type === 'local' &&
          !r.agenda_confirmed &&
          ((vs6430DeReportsSettings &&
            r.report_sub_type === ReportSubTypesEnum.ICSales &&
            r.status === SellerFilingReportStatusEnum.InstructionSent) ||
            (!!r.payment_instruction && !r.payment_instruction.cancelled))
      ),
    [ongoingFilingData, completedFilingData, vs6430DeReportsSettings]
  );

  const isOSSSelfService = useAppSelector(isOSSSelfServiceSelector);

  const currentOSSReport = useMemo(() => {
    return getCurrentOSSSeSeReport([
      ...(ongoingFilingData || []),
      ...(completedFilingData || []),
    ]);
  }, [ongoingFilingData, completedFilingData]);

  const currDU = useMemo(() => {
    return dataUnits?.find(
      (du) =>
        differenceInCalendarMonths(new Date(), normalizeDateFE(du.date)) === 1
    );
  }, [dataUnits]);

  const isMUNeeded = useMemo(
    () =>
      currDU?.items.some(
        (item) =>
          item.source_type === 'manual' &&
          item.status === DUItemStatusEnum.NEW &&
          !item.no_data_needed
      ),
    [currDU]
  );

  const isDUDataConfirmed =
    !!currDU &&
    !!currDU.confirmation_date &&
    currDU.correction_status === DataCorrectionStatusEnum.Blocked;

  const isToDoConfirmDataNeeded =
    !!currDU && !isDUDataConfirmed && currDate.getDate() >= 5;

  const hasDeEsReports = registeredVATIds?.data?.some(
    (item) =>
      item.Country.short_name === 'DE' || item.Country.short_name === 'ES'
  );

  const hasOngoingTasks = useMemo(
    () =>
      isMUNeeded ||
      !!countPP ||
      isToDoConfirmDataNeeded ||
      [
        ...(reportsToConfirmPayment || []),
        ...(reportsToConfirmAgenda || []),
      ].filter(
        (r) =>
          differenceInCalendarMonths(
            getUTCDate(),
            normalizeDateFE(r.payment_instruction?.due_date || r.deadline_date)
          ) <= 0
      ).length > 0,
    [
      countPP,
      isMUNeeded,
      reportsToConfirmAgenda,
      reportsToConfirmPayment,
      isToDoConfirmDataNeeded,
    ]
  );

  const OSSReports: NormalizedSellerFilingReport[] = useMemo(
    () =>
      [...(ongoingFilingData || []), ...(completedFilingData || [])].filter(
        (r) => r.type === 'oss' && !r.payment_instruction?.cancelled
      ),
    [ongoingFilingData, completedFilingData]
  );

  const currDUStatsData = currDUStats.data;
  const duStatsWithErrors = useMemo(() => {
    if (!currDUStatsData) return [];

    return currDUStatsData.filter((s) => s.errors_count > 0);
  }, [currDUStatsData]);

  if (isConnectingAmazon) return <Loader />;
  return (
    <PageLayout
      top={
        <>
          {vs4972NewYear2024 && (
            <NewYearIcon className='absolute right-14 top-0 h-20 w-20' />
          )}
          <div className='Text--30 mb-4 font-black'>{t('Global overview')}</div>
        </>
      }
      data-testid='seller-dashboard'
      className='flex-grow'
      rightColumn={
        <div
          style={{
            maxWidth: 328,
            paddingTop:
              hasOngoingTasks &&
              !isOSSSelfService &&
              !vs5919CpNotificationWidget
                ? 32
                : undefined,
          }}
          className='space-y-4 lg:ml-auto'
        >
          {vs3424MerryChristmas && <MerryChristmasBanner />}
          <FeedbackWidget testId='FeedbackWidget' />
          <div className='rounded-lg bg-white p-4'>
            <div className='flex items-center'>
              {user && (
                <Avatar
                  className='Avatar--SellerDashboard flex-shrink-0 text-sm'
                  data={user}
                  backgroundColor={getColor(user?.Name)}
                  testId={`participant-${user?.Id}`}
                />
              )}
              <div className='ml-2 truncate'>
                <div className='truncate font-semibold'>
                  {user?.Name} {user?.FamilyName}
                </div>
                <div className='truncate text-sm text-gray-700'>
                  {user?.Email}
                </div>
              </div>
            </div>
          </div>
          {reports.isLoading ? (
            <Shimmer className='SellerDashboard__loading--integrations' />
          ) : (
            <SellerCalendarCollapse reports={reports.data || []} />
          )}
          {isLoadingIntegrations ||
          easybill.isLoading ||
          billbee.isLoading ||
          jtlCredentials.isLoading ? (
            <Shimmer className='SellerDashboard__loading--integrations' />
          ) : (
            <div className='ml-auto rounded-lg bg-white pb-4 pt-2'>
              {(integrations ||
                activeCompany?.EasybillClient ||
                billbee.data ||
                jtlCredentials.data) && (
                <Integrations
                  integrations={integrations}
                  easybillData={easybill.data}
                  billbeeData={billbee.data}
                  jtlCredentials={jtlCredentials.data}
                />
              )}
            </div>
          )}
        </div>
      }
    >
      <div>
        {isLoadingPP ||
        isLoadingUploadedFiles ||
        isLoadingDataUnits ||
        ongoingFiling.isLoading ||
        completedFiling.isLoading ||
        currDUStats.isLoading ? (
          <div>
            <Shimmer className='SellerDashboard__loading mb-6' />
            <Shimmer className='SellerDashboard__loading mb-6' />
          </div>
        ) : (
          <>
            {vs5919CpNotificationWidget && (
              <NotificationWidget
                text='Upcoming VAT rate changes for 2025'
                title='Upcoming VAT changes for 2025'
              >
                <div className='space-y-2 divide-y-[1px] pb-4'>
                  <div className='space-y-2'>
                    <p>
                      {t(
                        'The following new VAT rates will be applicable in 2025'
                      )}
                      :
                    </p>
                    <ul className='list-disc pl-1'>
                      <span className='-ml-1 block pb-1 font-semibold'>
                        {t('From 1 January 2025')}:
                      </span>
                      <li className='ml-3 space-x-2'>
                        <span className='font-semibold'>{t('EE')}:</span>
                        <span>
                          {t(
                            'VAT on accommodation with breakfast will rise from 9% to 13%'
                          )}
                        </span>
                      </li>
                      <li className='ml-3 space-x-2'>
                        <span className='font-semibold'>{t('FI')}:</span>
                        <span>
                          {t('Reduced VAT rate will increase from 10% to 14%')}
                        </span>
                      </li>
                      <li className='ml-3 space-x-2'>
                        <span className='font-semibold'>{t('IL')}</span>:
                        <span>
                          {t('Standard VAT rate will rise from 17% to 18%')}
                        </span>
                      </li>
                      <li className='ml-3'>
                        <div className='flex flex-col '>
                          <span className='font-semibold'>{t('SK')}:</span>
                          <span>
                            -{' '}
                            {t(
                              'Standard VAT rate will increase from 20% to 23%'
                            )}
                          </span>
                          <span>
                            -{' '}
                            {t(
                              'Reduced VAT rate will increase from 10% to 19%'
                            )}
                          </span>
                        </div>
                      </li>
                      <li className='ml-3 space-x-2'>
                        <span className='font-semibold'>{t('CH')}:</span>
                        <span>
                          {t(
                            'A new VAT rate of 2.5% will apply to feminine hygiene products'
                          )}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className='space-y-2 pt-3'>
                    <p>{t('Changes in 2024')}:</p>
                    <ul className='list-disc pl-1'>
                      <span className='-ml-1 block pb-1 font-semibold'>
                        {t('From 1 September 2024')}:
                      </span>
                      <li className='ml-3 space-x-2'>
                        <span className='font-semibold'>{t('FI')}:</span>
                        <span>
                          {t('Standard VAT rate increased from 24% to 25.5%')}
                        </span>
                      </li>
                    </ul>
                    <ul className='list-disc pl-1'>
                      <span className='-ml-1 block pb-1 font-semibold'>
                        {t('From 1 October 2024')}:
                      </span>
                      <li className='ml-3 space-x-2'>
                        <span className='font-semibold'>{t('ES')}:</span>
                        <span>
                          {t(
                            'VAT on basic food products increased from 0% to 2% (applies only for Q4 2024, subject to further adjustments in 2025)'
                          )}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </NotificationWidget>
            )}
            <OpenTasks
              hasOnboardingTask={
                activeCompany?.PricingModel &&
                (activeCompany.PricingModel === PricingModelEnum.Core ||
                  activeCompany.PricingModel ===
                    PricingModelEnum.JTLIncludingOSSSeSe)
              }
              duStatsWithErrors={duStatsWithErrors}
              OSSReports={OSSReports}
              isOSSSelfService={isOSSSelfService || false}
              isMUNeeded={isMUNeeded}
              purchasePricesNeeded={typeof countPP === 'number' && countPP > 0}
              isDUDataConfirmed={isDUDataConfirmed}
              isToDoConfirmDataNeeded={isToDoConfirmDataNeeded}
              hasDeEsReports={hasDeEsReports}
              reportsToConfirmPayment={reportsToConfirmPayment}
              reportsToConfirmAgenda={reportsToConfirmAgenda}
              handleOpenPaymentInstructionModal={
                handleOpenPaymentInstructionModal
              }
              openSubmitBOPModal={openSubmitBOPModal}
            >
              <>
                {isOSSSelfService &&
                  (ongoingFiling.isLoading ? (
                    <div>
                      <Shimmer className='SellerDashboard__loading mb-6' />
                    </div>
                  ) : (
                    currentOSSReport && (
                      <OSSSeSeReportPreview
                        report={currentOSSReport}
                        link={{ to: AuthedRoutesEnum.OSSFiles }}
                      />
                    )
                  ))}
              </>
            </OpenTasks>
          </>
        )}
        {yearPayments.isLoading ||
        ongoingFiling.isLoading ||
        completedFiling.isLoading ? (
          <Shimmer className='SellerDashboard__loading--chart mb-6' />
        ) : (
          <SellerChart
            payments={
              yearPayments.data?.filter((a) => a.Amounts[0].amount > 0) || []
            }
            filingData={[
              ...(ongoingFiling.data || []),
              ...(completedFiling.data || []),
            ].filter((f) => !!f.payment_instruction)}
          />
        )}
      </div>
      <PaymentInstructionModal
        report={reportToConfirmPayment}
        isModalOpened={isPaymentInstructionModalOpened}
        closeModal={closePaymentInstructionModal}
        onClickConfirm={onClickConfirmPayment(reportToConfirmPayment)}
        isConfirming={isConfirmingPayment}
        isReportHasDisregardedInstruction={
          reportToConfirmPayment &&
          [
            ...new Set(
              [...(ongoingFilingData || []), ...(completedFilingData || [])]
                .filter((r) => r.payment_instruction?.cancelled)
                .map((r) => r.id)
            ),
          ].includes(reportToConfirmPayment.id)
        }
      />
      <SubmitBOPModal
        isModalOpened={isSubmitBOPModalOpened}
        closeModal={closeSubmitBOPModal}
        report={reportToSubmit}
        onSubmit={submitReport}
      />
    </PageLayout>
  );
}

export interface ChartItem {
  name: string;
  value: number;
  amounts?: {
    [key: string]: number;
  };
  originalCurrency?: string;
}
